/**
 * Endpoint defs for the availability-windows capability (availability-windows-model).
 *
 * Traceability: availability-windows / Requirement: Create availability window
 * with supersession; Delete availability window; List availability windows for a
 * unit; Bulk mark availability windows by selection.
 */
import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  AvailabilityWindowSourceSchema,
  AvailabilityWindowStateSchema,
} from '../schemas/availability-window.js';
import { BulkEditSelectionSchema } from '../schemas/media-units-bulk-edit.js';

const AvailabilityWindowIdSchema = z.coerce.number().int().positive();
const MediaUnitIdSchema = z.coerce.number().int().positive();

export const AvailabilityWindowRecordSchema = z.object({
  id: AvailabilityWindowIdSchema,
  mediaUnitId: MediaUnitIdSchema,
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  state: AvailabilityWindowStateSchema,
  source: AvailabilityWindowSourceSchema,
  importJobId: z.number().int().positive().nullable(),
  createdBy: z.number().int().positive().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type AvailabilityWindowRecord = z.infer<typeof AvailabilityWindowRecordSchema>;

// `id` is the path param (media unit id) — consumed by callEndpoint's path
// substitution and omitted from the wire body by the NestJS controller DTO
// (mirrors reservations.endpoints.ts's UpdateReservationDatesSchema pattern).
export const CreateAvailabilityWindowSchema = z.object({
  id: MediaUnitIdSchema,
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  state: AvailabilityWindowStateSchema,
});
export type CreateAvailabilityWindowBody = z.infer<typeof CreateAvailabilityWindowSchema>;

export const DeleteAvailabilityWindowSchema = z.object({
  id: MediaUnitIdSchema,
  windowId: AvailabilityWindowIdSchema,
});

export const AvailabilityWindowListQuerySchema = z.object({
  id: MediaUnitIdSchema,
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export type AvailabilityWindowListQuery = z.infer<typeof AvailabilityWindowListQuerySchema>;

export const AvailabilityWindowListResponseSchema = z.object({
  rows: z.array(AvailabilityWindowRecordSchema),
  total: z.number().int().nonnegative(),
});
export type AvailabilityWindowListResponse = z.infer<typeof AvailabilityWindowListResponseSchema>;

// Reuses the bulk-edit-media selection union verbatim ({ ids } | { filter }) —
// see packages/contracts/src/schemas/media-units-bulk-edit.ts. Filter-based
// selections are resolved server-side using the same MediaUnitFilter
// resolution as the inventory list endpoint.
export const BulkMarkAvailabilityRequestSchema = z.object({
  selection: BulkEditSelectionSchema,
  dateFrom: z.coerce.date(),
  dateTo: z.coerce.date(),
  state: AvailabilityWindowStateSchema,
});
export type BulkMarkAvailabilityRequest = z.infer<typeof BulkMarkAvailabilityRequestSchema>;

// Mirrors the bulk-edit-media response shape's optional failedIds field so the
// caller can identify which specific unit ids need a retry, not only the
// aggregate `failed` count.
export const BulkMarkAvailabilityResponseSchema = z.object({
  updated: z.number().int().min(0),
  failed: z.number().int().min(0),
  failedUnitIds: z.array(MediaUnitIdSchema).optional(),
});
export type BulkMarkAvailabilityResponse = z.infer<typeof BulkMarkAvailabilityResponseSchema>;

export const availabilityWindows = {
  create: endpoint(
    'POST',
    '/media-units/:id/availability-windows',
    CreateAvailabilityWindowSchema,
    AvailabilityWindowRecordSchema,
  ),
  delete: endpoint(
    'DELETE',
    '/media-units/:id/availability-windows/:windowId',
    DeleteAvailabilityWindowSchema,
    z.void(),
  ),
  list: endpoint(
    'GET',
    '/media-units/:id/availability-windows',
    AvailabilityWindowListQuerySchema,
    AvailabilityWindowListResponseSchema,
  ),
  bulkMark: endpoint(
    'POST',
    '/media-units/bulk-mark-availability',
    BulkMarkAvailabilityRequestSchema,
    BulkMarkAvailabilityResponseSchema,
  ),
};
